import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getCourseCurriculumGroups,
  getCourseCurriculumGroupsByView,
  getCourseProgress,
  CurriculumGroupDTO,
  StudentTopicProgressDTO,
} from '../services/curriculumService';
import {
  getCourseStructure,
  getStudentCourseStructure,
  getDistinctViews,
  CourseStructureNodeDTO
} from '../services/adminCourseService';
import { courseService } from '../services/courseService';
import { courseEnrollmentService } from '../services/courseEnrollmentService';
import { getActiveSessionInfo, ActiveSessionInfoDTO } from '../services/studentSessionService';
import { recommendationService, TopicRecommendation, DisplayChip } from '../services/recommendationService';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  CircularProgress,
  LinearProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Paper
} from '@mui/material';
import {
  ArrowBack,
  PlayArrow,
  CheckCircle,
  RadioButtonUnchecked,
  Assignment,
  Quiz,
  History,
  Settings,
  Person,
  Schedule,
  TrendingUp,
  MenuBook,
  ExpandLess,
  ExpandMore,
  FolderOpen,
  Article,
  LightbulbOutlined,
  Close,
  ErrorOutline,
  HourglassEmpty,
  CheckCircleOutline,
  ArrowForward
} from '@mui/icons-material';

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  userType?: string;
}

interface CurriculumAreaGroup {
  id: number;
  name: string;
  description: string;
  status: 'not_started' | 'in_progress' | 'completed';
  progress: number;
  totalTopics: number;
  completedTopics: number;
  lastAccessed?: string;
}

interface Course {
  id: number;
  name: string;
  description: string;
  status: string;
  progress: number;
  lastAccessed: string;
  instructor: string;
  duration: string;
  difficulty: string;
  curriculumAreaGroups: CurriculumAreaGroup[];
  deliveryType?: string;
  courseType?: string;
}

const normalizeTopicStatus = (
  status?: string
): 'not_started' | 'in_progress' | 'completed' => {
  if (!status) return 'not_started';
  const normalized = status.trim().toUpperCase().replace('-', '_');
  if (normalized === 'COMPLETED') return 'completed';
  if (normalized === 'IN_PROGRESS' || normalized === 'INPROGRESS') return 'in_progress';
  return 'not_started';
};

const mapGroupToCurriculumAreaGroup = (group: CurriculumGroupDTO): CurriculumAreaGroup => {
  let status: 'not_started' | 'in_progress' | 'completed' = 'not_started';
  if (group.progressPercentage >= 100) status = 'completed';
  else if (group.progressPercentage > 0) status = 'in_progress';

  return {
    id: group.id,
    name: group.name,
    description: group.description,
    status: status,
    progress: group.progressPercentage,
    totalTopics: group.totalTopics,
    completedTopics: group.completedTopics,
    lastAccessed: undefined
  };
};

/** Chip colour map for recommendation display categories. */
const REC_CHIP_CONFIG: Record<DisplayChip, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  READY:            { label: 'Ready',            color: '#00b894', bg: 'rgba(0,184,148,0.12)',  icon: <CheckCircleOutline fontSize="small" /> },
  IN_PROGRESS:      { label: 'In Progress',      color: '#0984e3', bg: 'rgba(9,132,227,0.12)',  icon: <HourglassEmpty fontSize="small" /> },
  STRUGGLING:       { label: 'Struggling',       color: '#e17055', bg: 'rgba(225,112,85,0.12)', icon: <ErrorOutline fontSize="small" /> },
  PREREQUISITE_GAP: { label: 'Prerequisite Gap', color: '#d63031', bg: 'rgba(214,48,49,0.12)',  icon: <ErrorOutline fontSize="small" /> },
};

const CourseDetails: React.FC<{ preview?: boolean }> = ({ preview = false }) => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [viewId, setViewId] = useState<number | null>(
    searchParams.get('viewId') ? parseInt(searchParams.get('viewId')!) : null
  );

  const [user, setUser] = useState<User | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState<ActiveSessionInfoDTO | null>(null);

  // Recommendation state (course-specific)
  const [recommendations, setRecommendations] = useState<TopicRecommendation[]>([]);
  const [recBannerDismissed, setRecBannerDismissed] = useState(false);

  // Graph Data
  const [structureNodes, setStructureNodes] = useState<CourseStructureNodeDTO[]>([]);
  const [availableViews, setAvailableViews] = useState<number[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Record<number, boolean>>({});

  // Maps for joining Data
  const [groupProgressMap, setGroupProgressMap] = useState<Record<number, CurriculumAreaGroup>>({});
  const [topicProgressMap, setTopicProgressMap] = useState<Record<number, StudentTopicProgressDTO>>({});

  // UI State
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (!preview && courseId) {
      getActiveSessionInfo(Number(courseId)).then(setActiveSession).catch(() => {});
    }
  }, [courseId, preview]);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);

        if (preview) {
          setUser({
            id: 0,
            username: 'preview.admin',
            email: 'admin@preview.com',
            firstName: 'Preview',
            lastName: 'Admin',
            role: 'SYSTEM_ADMIN',
            userType: 'SYSTEM_ADMIN'
          });
        } else {
          const userData = localStorage.getItem('user');
          if (userData) {
            setUser(JSON.parse(userData));
          }
        }

        const courseIdNum = parseInt(courseId || '1');

        let effectiveViewId = viewId;
        if (!preview && !effectiveViewId) {
          const userData = localStorage.getItem('user');
          if (userData) {
            const parsedUser = JSON.parse(userData);
            try {
              const enrollments = await courseEnrollmentService.getActiveEnrollments(parsedUser.id);
              const enrollment = enrollments.find(e => e.courseId === courseIdNum);
              if (enrollment?.preferredViewId) {
                effectiveViewId = enrollment.preferredViewId;
                setViewId(enrollment.preferredViewId);
              }
            } catch (e) {
              console.warn('Could not load preferred view from enrollment', e);
            }
          }
        }

        // Parallel Fetching
        const [courseInfo, views, graphNodes, curriculumGroups, topicProgressList] = await Promise.all([
          courseService.getCourseById(courseIdNum).catch(e => null),
          getDistinctViews(courseIdNum).catch(e => []),
          // Use Student Structure (Active Version) for students, or Admin Structure (Draft) for Preview
          preview
            ? getCourseStructure(courseIdNum, effectiveViewId || undefined).catch(e => { console.error("Admin Structure Fetch Error:", e); return []; })
            : getStudentCourseStructure(courseIdNum, effectiveViewId || undefined).catch(e => { console.error("Student Structure Fetch Error:", e); return []; }),
          effectiveViewId
            ? getCourseCurriculumGroupsByView(courseIdNum, effectiveViewId).catch(e => [])
            : getCourseCurriculumGroups(courseIdNum).catch(e => []),
          preview ? Promise.resolve([]) : getCourseProgress(courseIdNum).catch(e => []),
        ]);

        if (!preview && courseInfo?.courseType === 'ABACUS_MATHS') {
          navigate(`/student-courses/${courseIdNum}/abacus`, { replace: true });
          return;
        }

        // Process Views
        setAvailableViews(views);

        // Process Structure
        setStructureNodes(graphNodes || []);
        const initialExpanded: Record<number, boolean> = {};
        (graphNodes || []).forEach(node => {
          if (node.isExpanded) initialExpanded[node.id] = true;
        });
        setExpandedNodes(initialExpanded);

        // Process Progress Groups
        const curriculumAreaGroups = (curriculumGroups || []).map(mapGroupToCurriculumAreaGroup);
        const progressMap: Record<number, CurriculumAreaGroup> = {};
        curriculumAreaGroups.forEach(g => {
          progressMap[g.id] = g;
        });
        setGroupProgressMap(progressMap);

        const topicMap: Record<number, StudentTopicProgressDTO> = {};
        (topicProgressList || []).forEach((p) => {
          topicMap[p.topicId] = p;
        });
        setTopicProgressMap(topicMap);

        // Course Model
        const overallProgress = curriculumAreaGroups.length > 0
          ? Math.round(curriculumAreaGroups.reduce((sum, g) => sum + g.progress, 0) / curriculumAreaGroups.length)
          : 0;

        const deliveryType = (curriculumGroups && curriculumGroups.length > 0)
          ? (curriculumGroups[0]?.deliveryType || 'SELF_TUTORED')
          : 'SELF_TUTORED';
        const isSelfTutored = deliveryType === 'SELF_TUTORED';

        setCourse({
          id: courseIdNum,
          name: courseInfo?.name || 'Mathematics',
          description: courseInfo?.description || 'Mathematics course',
          status: courseInfo?.status || 'ACTIVE',
          progress: overallProgress,
          lastAccessed: new Date().toISOString().split('T')[0],
          instructor: isSelfTutored ? '' : 'Mathematics Teacher',
          duration: courseInfo?.durationWeeks ? `${courseInfo.durationWeeks} weeks` : '36 weeks',
          difficulty: courseInfo?.difficultyLevel || 'Intermediate',
          curriculumAreaGroups: curriculumAreaGroups,
          deliveryType: deliveryType,
          courseType: courseInfo?.courseType,
        });

      } catch (error) {
        console.error('Error fetching course data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId, viewId]);

  // Fetch course-specific recommendations for authenticated students
  useEffect(() => {
    if (preview || !courseId) return;
    const courseIdNum = parseInt(courseId);
    recommendationService.getCourseRecommendations(courseIdNum, 3)
      .then(recs => setRecommendations(recs))
      .catch(err => console.error('Course recommendation fetch failed', err));
  }, [courseId, preview]);

  const handleBackToDashboard = () => {
    navigate(preview ? '/admin/courses' : '/student-dashboard');
  };

  const handleToggleNode = (nodeId: number) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const handleNodeClick = (node: CourseStructureNodeDTO) => {
    if (node.nodeType === 'TOPIC_REF' && node.refId) {
      const subjectId = node.parentId || 0;
      navigate(`/student-courses/${courseId}/subjects/${subjectId}/topics/${node.refId}`);
    } else {
      handleToggleNode(node.id);
    }
  };

  const handleViewChange = async (newViewId: number | null) => {
    setViewId(newViewId);
    setSettingsOpen(false);
    if (newViewId) setSearchParams({ viewId: newViewId.toString() });
    else setSearchParams({});

    if (!preview && user?.id && courseId) {
      try {
        await courseEnrollmentService.updatePreferredView(user.id, parseInt(courseId), newViewId);
      } catch (e) {
        console.error('Failed to save preferred view', e);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle color="success" />;
      case 'in_progress': return <PlayArrow color="primary" />;
      default: return <RadioButtonUnchecked color="disabled" />;
    }
  };

  // --- Recursive Renderer with Rich Styling for Parents ---
  const renderTree = (nodes: CourseStructureNodeDTO[], depth = 0) => {
    if (!nodes || nodes.length === 0) return null;

    return (
      <List component="div" disablePadding>
        {nodes.map((node, index) => {
          if (node.isVisible === false) return null;

          const isExpanded = expandedNodes[node.id];
          const isTopic = node.nodeType === 'TOPIC_REF';
          const hasChildren = node.children && node.children.length > 0;

          // Try to find matching progress data if it's a wrapper/curriculum ref
          // Assuming 'refId' maps to GroupId for top-level nodes
          const progressData = (!isTopic && node.refId && groupProgressMap[node.refId])
            ? groupProgressMap[node.refId]
            : null;

          const topicProgress = (isTopic && node.refId && topicProgressMap[node.refId])
            ? topicProgressMap[node.refId]
            : null;
          const topicStatus = topicProgress ? normalizeTopicStatus(topicProgress.status) : 'not_started';

          // Use Rich "Card-like" List Item for Top Level (Depth 0)
          if (depth === 0) {
            return (
              <React.Fragment key={node.id}>
                <ListItem
                  disablePadding
                  sx={{
                    display: 'block',
                    mb: 2,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  {/* Rich Header Row */}
                  <Box
                    onClick={() => handleNodeClick(node)}
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      {progressData ? getStatusIcon(progressData.status) : (
                        isExpanded ? <ExpandLess color="primary" /> : <ExpandMore color="action" />
                      )}
                    </ListItemIcon>

                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                          {node.label || "Module"}
                        </Typography>
                        {progressData && (
                          <Chip
                            label={progressData.status.replace('_', ' ')}
                            size="small"
                            color={getStatusColor(progressData.status) as any}
                            variant="outlined"
                          />
                        )}
                        {!progressData && hasChildren && (
                          <Chip size="small" label={`${node.children?.length} Items`} variant="outlined" />
                        )}
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: '1.5em' }}>
                        {progressData?.description || "Curriculum Module"}
                      </Typography>

                      {/* Progress Bar Row */}
                      {progressData && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={progressData.progress}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 65, textAlign: 'right' }}>
                            {progressData.progress}%
                          </Typography>
                        </Box>
                      )}

                      {!progressData && !hasChildren && (
                        <Typography variant="caption" color="text.secondary">
                          No topics assigned
                        </Typography>
                      )}
                    </Box>

                    {/* Action Button for Card (Play/Expand) */}
                    <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNodeClick(node);
                        }}
                        color="primary"
                      >
                        {isExpanded ? <ExpandLess /> : <PlayArrow />}
                      </IconButton>
                    </Box>

                  </Box>

                  {/* Children (Collapsible) */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Divider />
                    <Box sx={{ bgcolor: 'background.default', p: 1, pl: 2 }}>
                      {renderTree(node.children!, depth + 1)}
                    </Box>
                  </Collapse>
                </ListItem>
              </React.Fragment>
            );
          }

          // Inner Nodes (Topics etc) - Clean List Look
          return (
            <React.Fragment key={node.id}>
              <ListItem
                button
                sx={{
                  pl: 2 + (depth * 2),
                  borderRadius: 1,
                  mb: 0.5,
                  backgroundColor: 'background.paper'
                }}
                onClick={() => handleNodeClick(node)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {isTopic ? getStatusIcon(topicStatus) : (
                    isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={node.label || (isTopic ? `Topic ${node.refId}` : 'Section')}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: isTopic ? 'normal' : '600'
                  }}
                />

                {isTopic && (
                  <Chip
                    label={topicStatus.replace('_', ' ')}
                    size="small"
                    color={getStatusColor(topicStatus) as 'default' | 'primary' | 'success'}
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                )}

                {/* Action Icon for Topics */}
                {isTopic && (
                  <IconButton size="small" edge="end">
                    <PlayArrow fontSize="small" />
                  </IconButton>
                )}
              </ListItem>

              {hasChildren && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  {renderTree(node.children!, depth + 1)}
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  // --- Main Render ---

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user || !course) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Course access error.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" onClick={() => navigate('/student-courses')} sx={{ cursor: 'pointer' }}>
            My Courses
          </Link>
          <Typography color="text.primary">{course.name}</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <IconButton onClick={() => navigate('/student-courses')} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" component="h1" gutterBottom>
              {course.name}
              {viewId && <Chip label={`View: ${viewId}`} size="small" sx={{ ml: 2, verticalAlign: 'middle' }} color="secondary" />}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {course.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
              <Chip label={course.status} color={course.status === 'ACTIVE' ? 'success' : 'default'} size="small" />
              <Chip label={course.difficulty} color="info" size="small" />
              <Chip label={course.duration} color="secondary" size="small" />
            </Box>
          </Grid>
          <Grid item>
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
              {!preview && activeSession && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlayArrow />}
                  onClick={() => navigate(`/courses/${courseId}/playground`)}
                >
                  Join Session
                </Button>
              )}
              {!preview && (
                <Button
                  variant="outlined"
                  startIcon={<History />}
                  onClick={() => navigate(`/student-courses/${courseId}/quiz-history`)}
                >
                  Quiz history
                </Button>
              )}
              <Tooltip title="View Settings">
                <Button
                  variant="outlined"
                  startIcon={<Settings />}
                  onClick={() => setSettingsOpen(true)}
                >
                  Views
                </Button>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)}>
        <DialogTitle>Course View Settings</DialogTitle>
        <DialogContent sx={{ minWidth: 300, mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select a different structure view for this course.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select View</InputLabel>
            <Select
              value={viewId || ''}
              label="Select View"
              onChange={(e) => handleViewChange(e.target.value === '' ? null : Number(e.target.value))}
            >
              <MenuItem value=""><em>Default View</em></MenuItem>
              {availableViews.map((v) => (
                <MenuItem key={v} value={v}>View {v}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* "What to learn next" banner — shown when recommendations exist and not dismissed */}
      {!preview && !recBannerDismissed && recommendations.length > 0 && (
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(108,99,255,0.06) 0%, rgba(0,210,211,0.06) 100%)',
            border: '1px solid rgba(108,99,255,0.15)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LightbulbOutlined sx={{ color: '#fdcb6e', fontSize: 20 }} />
              What to learn next
            </Typography>
            <IconButton size="small" onClick={() => setRecBannerDismissed(true)} aria-label="Dismiss recommendations">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {recommendations.map(rec => {
              const chip = REC_CHIP_CONFIG[rec.displayChip as DisplayChip] ?? REC_CHIP_CONFIG.READY;
              return (
                <Box
                  key={rec.topicId}
                  sx={{
                    flex: '1 1 200px',
                    minWidth: 180,
                    maxWidth: 300,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: chip.color, boxShadow: 2 },
                  }}
                  onClick={() => {
                    // Navigate to the course — student can find the topic in the full structure below
                    navigate(`/student-courses/${rec.courseId}`);
                  }}
                >
                  {/* Chip */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1,
                             color: chip.color, bgcolor: chip.bg, borderRadius: 1.5, px: 1, py: 0.3, width: 'fit-content' }}>
                    {chip.icon}
                    <Typography variant="caption" fontWeight="bold">{chip.label}</Typography>
                  </Box>

                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                    {rec.topicName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                    {rec.reason}
                  </Typography>

                  {rec.lastQuizScore !== null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Score: <strong
                          style={{ color: rec.lastQuizScore >= rec.passThreshold ? '#00b894' : '#e17055' }}>
                          {rec.lastQuizScore.toFixed(0)}%
                        </strong> / {rec.passThreshold}% needed
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Main Content Area */}
        <Grid item xs={12} md={8}>

          {/* Graph Structure Rendered as Rich Cards */}
          {structureNodes.length > 0 ? (
            <Box>
              {renderTree(structureNodes)}
            </Box>
          ) : (
            /* Fallback to legacy List if Graph is empty but Groups exist */
            <Card>
              <CardContent>
                <List>
                  {course.curriculumAreaGroups.length > 0 ? (
                    course.curriculumAreaGroups.map((group) => (
                      <ListItem
                        key={group.id}
                        divider
                        secondaryAction={
                          <IconButton edge="end" onClick={() => navigate(`/student-courses/${courseId}/subjects/${group.id}`)}>
                            <PlayArrow />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={group.name}
                          secondary={`${group.totalTopics} topics • ${group.progress}% complete`}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography align="center" color="textSecondary" gutterBottom>
                        This course hasn't been published yet.
                      </Typography>
                      <Typography align="center" variant="body2" color="textSecondary">
                        Topic content will appear here once your tutor publishes a version.
                      </Typography>
                    </Box>
                  )}
                </List>
              </CardContent>
            </Card>
          )}

        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Course Progress Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Overall Progress</Typography>
                <Typography variant="body2" color="text.secondary">{course.progress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={course.progress} sx={{ height: 8, borderRadius: 4, mb: 1 }} />
              {course.lastAccessed && (
                <Typography variant="caption" color="text.secondary" display="block" align="right">
                  Last accessed: {course.lastAccessed}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Button variant="contained" fullWidth startIcon={<PlayArrow />}>Resume Learning</Button>
                <Button variant="outlined" fullWidth startIcon={<Assignment />}>Assignments</Button>
                <Button variant="outlined" fullWidth startIcon={<Quiz />}>Quizzes</Button>
              </Box>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Course Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" fontSize="small" />
                  <Typography variant="body2"><strong>Instructor:</strong> {course.instructor || 'Self-Paced'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule color="primary" fontSize="small" />
                  <Typography variant="body2"><strong>Duration:</strong> {course.duration}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MenuBook color="primary" fontSize="small" />
                  <Typography variant="body2"><strong>Modules:</strong> {structureNodes.length || course.curriculumAreaGroups.length}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CourseDetails;
